/**
 * 参加者画面の背景画像を決める。
 *
 * 決め方
 *   1. 画像の一覧を、イベントIDを種にした乱数で並べ替える（イベントごとに並びが変わる）
 *   2. サーバーが返す通し番号（backgroundSlot）番目の画像を使う。枚数を超えたら先頭へ戻る
 *
 * 通し番号はチーム戦ならチーム、個人戦なら参加者の単位で、イベント内で 0 から重複なく振られる。
 * そのため、採点対象の数が画像の枚数以下なら背景はかぶらない。
 * 超えた場合も、各画像の使用回数の差は最大1回に収まる。
 *
 * 番号・イベントID・画像の一覧が同じなら結果も同じなので、再読み込みしても背景は変わらない。
 * 逆に、画像の枚数を変えてデプロイすると割り当ても変わる。開催中にはデプロイしないこと。
 */

/*
 * src/assets/backgrounds/ の WebP をすべて拾う。
 * ファイル名にハッシュが付いた URL になるため、長くキャッシュしてよい。
 * ここで得るのは URL だけで、実際に読み込むのは選ばれた1枚のみ。
 */
const modules = import.meta.glob<string>("../assets/backgrounds/*.webp", {
  eager: true,
  query: "?url",
  import: "default",
});

/** ファイル名順に並べた背景画像の URL。並び順が決め方の前提になるため、必ず並べ替える */
export const BACKGROUND_URLS: readonly string[] = Object.keys(modules)
  .sort()
  .map((key) => modules[key] as string);

/** 文字列から 32bit の種を作る（FNV-1a） */
function hashString(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** 種から決まった順に 0 以上 1 未満の値を返す乱数（mulberry32） */
function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** イベントIDを種に並べ替える（Fisher-Yates）。元の配列は変えない */
export function shuffleForEvent<T>(items: readonly T[], eventId: string): T[] {
  const result = [...items];
  const random = seededRandom(hashString(eventId));
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j] as T, result[i] as T];
  }
  return result;
}

/**
 * 背景画像の URL を返す。画像が1枚もない、または番号が不正なら null（既定の背景色のまま）。
 */
export function pickBackground(
  eventId: string,
  slot: number | null | undefined,
  urls: readonly string[] = BACKGROUND_URLS
): string | null {
  if (urls.length === 0) return null;
  if (typeof slot !== "number" || !Number.isInteger(slot) || slot < 0) return null;
  const order = shuffleForEvent(urls, eventId);
  return order[slot % order.length] ?? null;
}

/**
 * RGBA の画素列の平均色を CSS の色で返す。画素が無ければ null。
 * 背景画像の下端の色を、ページの地の色に使うためのもの（components/Backdrop.tsx）。
 */
export function averageColor(data: ArrayLike<number>): string | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    r += data[i] as number;
    g += data[i + 1] as number;
    b += data[i + 2] as number;
    n++;
  }
  if (n === 0) return null;
  return `rgb(${Math.round(r / n)} ${Math.round(g / n)} ${Math.round(b / n)})`;
}
